import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Col, Container, Modal, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { debounce } from 'lodash';
import { DataRetriever } from '../../api/DataRetriever';
import { useToasts } from '../../contexts/toasts';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import AppSearch from '../../components/common/AppSearch';
import { DataspaceConnector } from '../../models/DataSpace';
import { TailSpin } from 'react-loader-spinner';
import DataspaceConnectorItem from '../../components/list-items/DataspaceConnectorItem';
import DataSpaceConnectorForm from '../../components/forms/DataSpaceConnectorForm';

export default function DataspaceConnectorsList() {
  const [connectors, setConnectors] = useState<DataspaceConnector[]>();
  const [searchText, setSearchText] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editing, setEditing] = useState<number>();
  const [connector, setConnector] = useState<DataspaceConnector>();

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const filteredConnectors = useMemo(() => connectors?.filter(r => r.name.toLowerCase().includes(searchText.toLowerCase())), [connectors, searchText]);

  const initConnectors = useCallback(() => {
    DataRetriever.getDataspaceConnectors()
      .then(
        ({ data }) => setConnectors(data),
        (err) => {
          setConnectors([]);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('connectors-error', message, `Data Space Connector Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [showToast]);

  useEffect(() => initConnectors(), [initConnectors]);

  const reset = useCallback(() => {
    setConnectors(undefined);
    initConnectors();
  }, [initConnectors]);

  const handleClose = useCallback(() => {
    setShowEditModal(false)
    setShowDeleteModal(false);
    setConnector(undefined);
    setEditing(undefined);
  }, []);

  const openCreateModal = useCallback(() => setShowEditModal(true), []);

  const handleEdit = useCallback((conn: DataspaceConnector, index: number) => () => {
    setEditing(index);
    setConnector(conn);
    setShowEditModal(true);
  }, []);

  const handleDelete = useCallback((conn: DataspaceConnector) => () => {
    setConnector(conn);
    setShowDeleteModal(true);
  }, []);

  const handleSubmit = useCallback((values: DataspaceConnector) => {
    showLoader();
    const promise = editing !== undefined
      ? DataRetriever.editDataspaceConnector(values!)
      : DataRetriever.createDataspaceConnector(values!);

    promise.then(
      () => {
        hideLoader();
        showToast('connector-success', `Connector ${editing !== undefined ? 'updated' : 'created'} successfully`, `Connector ${editing !== undefined ? 'Updated' : 'Created'}`, 'success');
        handleClose();
        reset();
      },
      (err) => {
        hideLoader();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('connector-error', message, `Connectors Error ${status ? `(${status})` : ''}`, 'danger');
      }
    )
  }, [handleClose, showLoader, hideLoader, reset, showToast, editing]);

  const handleSearchChange = useCallback((value: string) => setSearchText(value), []);
  const debounceSearchChange = useMemo(() => debounce(handleSearchChange, 500), [handleSearchChange]);

  const deleteConnector = useCallback(() => {
    showLoader();

    DataRetriever.deleteDataspaceConnnector(connector!.id)
      .then(
        () => {
          hideLoader();
          handleClose();
          reset();
          showToast('connector-success', 'Data Space Connector deleted successfully', 'Data Space Connector Success', 'success');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('connector-error', message, `Data Space Connector Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [showLoader, hideLoader, handleClose, connector, reset, showToast]);

  const renderAsyncContent = useCallback(() => {
    if (!filteredConnectors) return (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin
          height={100}
          width={100}
          color="#677ab1"
          radius="1"
        />
      </div>
    )

    return filteredConnectors?.length === 0
      ? (
        <Alert className="py-2" variant="info">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No connectors found.</span>
        </Alert>
      )
      : (
        <Row>
          {filteredConnectors!.map((connector, index) => (
            <Col key={connector.id} md={3} className="px-2 mb-3">
              <DataspaceConnectorItem
                className="h-100"
                connector={connector}
                onEdit={handleEdit(connector, index)}
                onDelete={handleDelete(connector)}
              />
            </Col>
          ))}
        </Row>
      )
  }, [filteredConnectors, handleDelete, handleEdit]);

  return (
    <Container className="mt-3">
      <Row className="mb-4 align-items-center">
        <Col>
          <h4 className="mb-0">Data Space Connectors List</h4>
        </Col>

        <Col>
          <AppSearch placeholder="Search connector..." onChange={debounceSearchChange} />
        </Col>

        <Col xs={6} md={4} className="text-end">
          <Button size="sm" variant="success" onClick={openCreateModal}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="d-none d-sm-inline ms-2">Create Data Space Connector</span>
          </Button>
        </Col>
      </Row>

      {renderAsyncContent()}

      <Modal show={showEditModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{editing !== undefined ? 'Edit Data Space Connector' : 'New Data Space Connector'}</Modal.Title>
        </Modal.Header>
        <DataSpaceConnectorForm connector={connector} onSubmit={handleSubmit} onCancel={handleClose} />
      </Modal>

      <Modal show={showDeleteModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Data Space Connector</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete data space connector <b>{connector?.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleClose}>
            Close
          </Button>
          <Button variant="danger" size="sm" onClick={deleteConnector}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}